import type { ReactNode } from "react";
import { useLanguage } from "../i18n/language-provider";
import type { PageName } from "../navigation/pages";
import { ReloadOffer } from "./ReloadOffer";
import { RenderFaultBoundary } from "./RenderFaultBoundary";

/**
 * Keeps a failed page from taking the application down with it.
 *
 * Without a boundary anywhere, a rejected route import reached the root and
 * React unmounted the whole tree — the user got a blank window with no way
 * back but a refresh they had to think of themselves. `staleChunkRecovery`
 * handles the common cause silently; what arrives here is what survived it,
 * so the offer is still a reload but the shell stays up and the sidebar
 * still navigates.
 *
 * It is the boundary every contained part of the app is drawn in
 * (`RenderFaultBoundary`), with the page's own answers to its two questions.
 * What stands in the page's place is the offer to reload, the one the app's
 * fallback draws for the whole window (`ReloadOffer`). And nothing is tried
 * again: a failed page is left for another one, and coming back to it mounts
 * it afresh, each page being a slot of its own in `PageRouter`.
 *
 * Each page is a part of its own in the report (`page.strategies`). A fault is
 * reported once a page load under the part it cost, so one part for every
 * page would take "Cannot read properties of undefined" on the Prices page
 * for the one already reported from Strategies.
 */

interface PageErrorBoundaryProps {
    children: ReactNode;
    /** The page drawn inside, as its report names it. */
    page: PageName;
}

function PageErrorFallback() {
    const { t } = useLanguage();

    return (
        <ReloadOffer
            title={t.app.pageLoadFailedTitle}
            message={t.app.pageLoadFailedMessage}
            reloadLabel={t.app.pageLoadFailedReload}
        />
    );
}

export function PageErrorBoundary({ children, page }: PageErrorBoundaryProps) {
    return (
        <RenderFaultBoundary part={`page.${page}`} fallback={<PageErrorFallback />}>
            {children}
        </RenderFaultBoundary>
    );
}
